import { useState } from 'react';
import { useAuth } from '../components/auth-context';
import { authApi } from '@/api/auth';
import { getApiErrorMessage } from '@/api/errors';
import logoImage from '@/assets/e363e0ec4c709fb1c7041e0e9bd4eeb51f44cd58.png';
import { Lock, Eye, EyeOff, ArrowLeft, Mail } from 'lucide-react';

export function LockScreen() {
  const { authState, currentUser, unlockApp, signOut, signIn, goToForgotPassword, goBackFromForgot } = useAuth();
  const isLocked = authState === 'locked';
  const isSignedOut = authState === 'signed-out';
  const isForgotPassword = authState === 'forgot-password';

  const [password, setPassword] = useState('');
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [resetSent, setResetSent] = useState(false);

  // Owner/admin login lives in rebookt-web — link there for staff who landed on the wrong app.
  const webUrl = ((import.meta.env.VITE_WEB_URL as string | undefined) || 'http://localhost:5173').replace(/\/+$/, '');
  const ownerLoginUrl = `${webUrl}/login`;

  const handleUnlock = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    const errorMsg = await unlockApp(password);
    if (errorMsg) {
      setError(errorMsg);
    }
    setPassword('');
    setIsLoading(false);
  };

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!email.trim()) {
      setError('Por favor ingresa tu correo electrónico.');
      return;
    }
    setIsLoading(true);
    const errorMsg = await signIn(email, password);
    if (errorMsg) {
      setError(errorMsg);
    }
    setPassword('');
    setIsLoading(false);
  };

  const handleSignOut = () => {
    setPassword('');
    setEmail('');
    setError('');
    signOut();
  };

  const handleForgotPassword = () => {
    setError('');
    setResetSent(false);
    goToForgotPassword();
  };

  const handleBackFromForgot = () => {
    setError('');
    setResetSent(false);
    setEmail('');
    goBackFromForgot();
  };

  const handleSendResetLink = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!email.trim()) {
      setError('Por favor ingresa tu correo electrónico.');
      return;
    }
    setIsLoading(true);
    try {
      await authApi.forgotPassword(email.trim());
      // Backend returns a generic message whether or not the account exists.
      setResetSent(true);
    } catch (err) {
      setError(getApiErrorMessage(err, 'No se pudo enviar el enlace. Inténtalo de nuevo.'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4"
      style={{ backgroundColor: 'var(--cream)' }}
    >
      <div className="w-full" style={{ maxWidth: '420px' }}>
        {/* Logo */}
        <div className="flex justify-center mb-8">
          <img
            src={logoImage}
            alt="RebookT"
            style={{ height: '40px', objectFit: 'contain' }}
          />
        </div>

        {/* ========== FORGOT PASSWORD ========== */}
        {isForgotPassword && (
          <>
            <div className="text-center mb-8">
              <h1 className="mb-2">Olvidé mi contraseña</h1>
              <p style={{ color: 'var(--muted-fg)', fontSize: 'var(--text-md)' }}>
                Ingresa tu correo y te enviaremos un enlace para restablecer tu contraseña.
              </p>
            </div>

            {resetSent ? (
              /* Success state */
              <div className="text-center">
                <div
                  className="mx-auto flex items-center justify-center rounded-full mb-4"
                  style={{
                    width: '56px',
                    height: '56px',
                    backgroundColor: 'rgba(224, 123, 84, 0.1)',
                  }}
                >
                  <Mail style={{ width: '24px', height: '24px', color: 'var(--terracotta)' }} />
                </div>
                <h3 className="mb-2">Revisa tu bandeja de entrada</h3>
                <p style={{ color: 'var(--muted-fg)', fontSize: 'var(--text-md)' }}>
                  Hemos enviado un enlace de recuperación a <span style={{ color: 'var(--dark-teal)', fontWeight: 'var(--font-weight-medium)' }}>{email}</span>
                </p>

                <button
                  type="button"
                  onClick={handleBackFromForgot}
                  className="flex items-center justify-center gap-2 mx-auto mt-6 bg-transparent border-none cursor-pointer hover:opacity-70 transition-opacity"
                  style={{
                    fontSize: 'var(--text-base)',
                    color: 'var(--terracotta)',
                    padding: 0,
                  }}
                >
                  <ArrowLeft style={{ width: '14px', height: '14px' }} />
                  Volver a iniciar sesión
                </button>
              </div>
            ) : (
              /* Email form */
              <form onSubmit={handleSendResetLink}>
                <div className="mb-4">
                  <label
                    className="block mb-1.5"
                    style={{ fontSize: 'var(--text-base)', fontWeight: 'var(--font-weight-medium)' }}
                  >
                    Correo electrónico
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => { setEmail(e.target.value); setError(''); }}
                    placeholder="correo@ejemplo.com"
                    className="w-full border rounded-lg"
                    style={{
                      height: 'var(--input-height)',
                      padding: '0 16px',
                      borderColor: error ? 'var(--destructive)' : 'var(--border-color)',
                      backgroundColor: 'var(--off-white)',
                      borderRadius: 'var(--radius-sm)',
                    }}
                    autoFocus
                  />
                </div>

                {error && (
                  <p className="mb-3" style={{ fontSize: 'var(--text-sm)', color: 'var(--destructive)' }}>
                    {error}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={!email || isLoading}
                  className="w-full flex items-center justify-center gap-2 text-white cursor-pointer transition-opacity hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{
                    height: 'var(--button-height)',
                    backgroundColor: 'var(--terracotta)',
                    fontSize: 'var(--text-md)',
                    fontWeight: 'var(--font-weight-medium)',
                    borderRadius: 'var(--radius-pill)',
                    border: 'none',
                  }}
                >
                  {isLoading ? (
                    <span className="inline-block w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    'Enviar enlace de recuperación'
                  )}
                </button>

                <div className="text-center mt-5">
                  <button
                    type="button"
                    onClick={handleBackFromForgot}
                    className="flex items-center justify-center gap-2 mx-auto bg-transparent border-none cursor-pointer hover:opacity-70 transition-opacity"
                    style={{
                      fontSize: 'var(--text-base)',
                      color: 'var(--terracotta)',
                      padding: 0,
                    }}
                  >
                    <ArrowLeft style={{ width: '14px', height: '14px' }} />
                    Volver a iniciar sesión
                  </button>
                </div>
              </form>
            )}
          </>
        )}

        {/* ========== LOCK SCREEN ========== */}
        {isLocked && (
          <>
            <div className="text-center mb-8">
              <h1 className="mb-2">App bloqueada</h1>
              <p style={{ color: 'var(--muted-fg)', fontSize: 'var(--text-md)' }}>
                Ingresa tu contraseña para continuar
              </p>
            </div>

            {/* User identity chip */}
            <div className="flex items-center justify-center gap-3 mb-6 min-w-0">
              <div
                className="rounded-full flex items-center justify-center text-white flex-shrink-0"
                style={{
                  width: '40px',
                  height: '40px',
                  backgroundColor: 'var(--terracotta)',
                  fontSize: 'var(--text-sm)',
                  fontWeight: 'var(--font-weight-medium)',
                }}
              >
                {currentUser?.initials}
              </div>
              <div className="min-w-0">
                <div
                  className="truncate"
                  style={{
                    fontFamily: 'var(--font-heading)',
                    fontSize: 'var(--text-lg)',
                    fontWeight: 'var(--font-weight-bold)',
                    color: 'var(--dark-teal)',
                  }}
                >
                  {currentUser?.fullName}
                </div>
                <div className="truncate" style={{ fontSize: 'var(--text-sm)', color: 'var(--muted-fg)' }}>
                  {currentUser?.email}
                </div>
              </div>
            </div>

            <form onSubmit={handleUnlock}>
              <div className="mb-2">
                <div className="flex items-center justify-between mb-1.5">
                  <label style={{ fontSize: 'var(--text-base)', fontWeight: 'var(--font-weight-medium)' }}>
                    Contraseña
                  </label>
                  <button
                    type="button"
                    onClick={handleForgotPassword}
                    className="bg-transparent border-none cursor-pointer hover:opacity-70 transition-opacity"
                    style={{
                      fontSize: 'var(--text-sm)',
                      color: 'var(--terracotta)',
                      padding: 0,
                    }}
                  >
                    ¿Olvidaste tu contraseña?
                  </button>
                </div>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => { setPassword(e.target.value); setError(''); }}
                    placeholder="Ingresa tu contraseña"
                    className="w-full border rounded-lg pr-10"
                    style={{
                      height: 'var(--input-height)',
                      padding: '0 40px 0 16px',
                      borderColor: error ? 'var(--destructive)' : 'var(--border-color)',
                      backgroundColor: 'var(--off-white)',
                      borderRadius: 'var(--radius-sm)',
                    }}
                    autoFocus
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 bg-transparent border-none cursor-pointer p-0"
                    style={{ color: 'var(--muted-fg)' }}
                  >
                    {showPassword
                      ? <EyeOff style={{ width: '18px', height: '18px' }} />
                      : <Eye style={{ width: '18px', height: '18px' }} />
                    }
                  </button>
                </div>
              </div>

              {error && (
                <p className="mb-3" style={{ fontSize: 'var(--text-sm)', color: 'var(--destructive)' }}>
                  {error}
                </p>
              )}

              <button
                type="submit"
                disabled={!password || isLoading}
                className="w-full flex items-center justify-center gap-2 text-white cursor-pointer transition-opacity hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed mt-4"
                style={{
                  height: 'var(--button-height)',
                  backgroundColor: 'var(--terracotta)',
                  fontSize: 'var(--text-md)',
                  fontWeight: 'var(--font-weight-medium)',
                  borderRadius: 'var(--radius-pill)',
                  border: 'none',
                }}
              >
                {isLoading ? (
                  <span className="inline-block w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <>
                    <Lock style={{ width: '16px', height: '16px' }} />
                    Desbloquear
                  </>
                )}
              </button>
            </form>

            <div className="text-center mt-5">
              <button
                type="button"
                onClick={handleSignOut}
                className="bg-transparent border-none cursor-pointer hover:opacity-70 transition-opacity"
                style={{
                  fontSize: 'var(--text-base)',
                  color: 'var(--terracotta)',
                  padding: 0,
                }}
              >
                Cerrar sesión
              </button>
            </div>
          </>
        )}

        {/* ========== SIGN IN SCREEN ========== */}
        {isSignedOut && (
          <>
            <div className="text-center mb-8">
              <h1 className="mb-2">Bienvenido de vuelta</h1>
              <p style={{ color: 'var(--muted-fg)', fontSize: 'var(--text-md)' }}>
                Inicia sesión para gestionar tu negocio
              </p>
            </div>

            <form onSubmit={handleSignIn}>
              <div className="mb-4">
                <label
                  className="block mb-1.5"
                  style={{ fontSize: 'var(--text-base)', fontWeight: 'var(--font-weight-medium)' }}
                >
                  Correo electrónico
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); setError(''); }}
                  placeholder="correo@ejemplo.com"
                  className="w-full border rounded-lg"
                  style={{
                    height: 'var(--input-height)',
                    padding: '0 16px',
                    borderColor: 'var(--border-color)',
                    backgroundColor: 'var(--off-white)',
                    borderRadius: 'var(--radius-sm)',
                  }}
                  autoFocus
                />
              </div>

              <div className="mb-2">
                <div className="flex items-center justify-between mb-1.5">
                  <label style={{ fontSize: 'var(--text-base)', fontWeight: 'var(--font-weight-medium)' }}>
                    Contraseña
                  </label>
                  <button
                    type="button"
                    onClick={handleForgotPassword}
                    className="bg-transparent border-none cursor-pointer hover:opacity-70 transition-opacity"
                    style={{
                      fontSize: 'var(--text-sm)',
                      color: 'var(--terracotta)',
                      padding: 0,
                    }}
                  >
                    ¿Olvidaste tu contraseña?
                  </button>
                </div>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => { setPassword(e.target.value); setError(''); }}
                    placeholder="••••••••"
                    className="w-full border rounded-lg pr-10"
                    style={{
                      height: 'var(--input-height)',
                      padding: '0 40px 0 16px',
                      borderColor: error ? 'var(--destructive)' : 'var(--border-color)',
                      backgroundColor: 'var(--off-white)',
                      borderRadius: 'var(--radius-sm)',
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 bg-transparent border-none cursor-pointer p-0"
                    style={{ color: 'var(--muted-fg)' }}
                  >
                    {showPassword
                      ? <EyeOff style={{ width: '18px', height: '18px' }} />
                      : <Eye style={{ width: '18px', height: '18px' }} />
                    }
                  </button>
                </div>
              </div>

              {error && (
                <p className="mb-3" style={{ fontSize: 'var(--text-sm)', color: 'var(--destructive)' }}>
                  {error}
                </p>
              )}

              <button
                type="submit"
                disabled={!email.trim() || !password || isLoading}
                className="w-full flex items-center justify-center gap-2 text-white cursor-pointer transition-opacity hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed mt-4"
                style={{
                  height: 'var(--button-height)',
                  backgroundColor: 'var(--terracotta)',
                  fontSize: 'var(--text-md)',
                  fontWeight: 'var(--font-weight-medium)',
                  borderRadius: 'var(--radius-pill)',
                  border: 'none',
                }}
              >
                {isLoading ? (
                  <span className="inline-block w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  'Iniciar sesión'
                )}
              </button>
            </form>

            <div
              className="text-center mt-5 pt-4"
              style={{ borderTop: '1px solid var(--border-color)' }}
            >
              <a
                href={ownerLoginUrl}
                style={{ fontSize: 'var(--text-base)', color: 'var(--muted-fg)', textDecoration: 'none' }}
              >
                ¿Eres dueño o administrador?{' '}
                <span style={{ color: 'var(--terracotta)', fontWeight: 'var(--font-weight-medium)' }}>
                  Inicia sesión aquí →
                </span>
              </a>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
